import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { debounce } from 'lodash'
import {
  LBInput,
  LBLoader,
  LBXIcon,
  LBButton,
  LBSwitch,
  LBRocketIcon
} from '@linearb/buzz'
import { getFeatureFlags } from '../apis/featureFlags'
import { getAdoptionPercentage } from '../utils/featureFlags'
import JiraFeatureRolloutSheet from './JiraFeatureRolloutSheet'
import EnablementBadge from './EnablementBadge'
import FeatureFlagStatusBadge from './FeatureFlagStatusBadge'
import type { FeatureFlagStatus } from '../constants/featureFlagStatus'
import InfoTooltip from './InfoTooltip'

interface FeatureFlag {
  id: string | number
  name: string
  subtext?: string
  feature_flag_on?: number
  feature_flag_off?: number
  default_value?: boolean
  total_organizations?: number
  status?: FeatureFlagStatus | null
}

interface FeatureFlagsResponse {
  results?: FeatureFlag[]
  data?: FeatureFlag[]
}

interface FeatureFlagFilterProps {
  onChange: (value: string | null) => void
  onFlagSelect?: (flag: FeatureFlag | null) => void
  inverted?: boolean
  onInvertedChange?: (inverted: boolean) => void
  initialValue?: string | null
}

const FeatureFlagFilter: React.FC<FeatureFlagFilterProps> = ({
  onChange,
  onFlagSelect,
  inverted = false,
  onInvertedChange,
  initialValue
}) => {
  const [inputValue, setInputValue] = useState(initialValue || '')
  const [suggestions, setSuggestions] = useState<FeatureFlag[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null)
  const [jiraSheetOpen, setJiraSheetOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Hydrate selectedFlag from initialValue on mount (intentionally runs once)
  useEffect(() => {
    if (!initialValue) {
      return
    }
    let cancelled = false
    const hydrate = async () => {
      try {
        const { data } = (await getFeatureFlags({
          searchParam: initialValue
        })) as { data: FeatureFlagsResponse }
        if (cancelled) {
          return
        }
        const flags = data?.results || (data as unknown as FeatureFlag[]) || []
        const match = flags.find(f => f.name === initialValue)
        if (match) {
          setSelectedFlag(match)
          onFlagSelect?.(match)
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to hydrate feature flag:', error)
        }
      }
    }
    hydrate()
    return () => {
      cancelled = true
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch feature flags matching the search term
  const fetchFeatureFlags = useCallback(
    async (searchValue: string): Promise<void> => {
      if (!searchValue || searchValue.length < 2) {
        setSuggestions([])
        setShowDropdown(false)
        return
      }

      setIsLoading(true)
      try {
        const { data } = (await getFeatureFlags({
          searchParam: searchValue
        })) as { data: FeatureFlagsResponse }
        const flags = data?.results || (data as unknown as FeatureFlag[]) || []
        flags.sort((a, b) => Number(b.id) - Number(a.id))
        setSuggestions(flags)
        setShowDropdown(flags.length > 0)
      } catch (error) {
        console.error('Failed to fetch feature flags:', error)
        setSuggestions([])
        setShowDropdown(false)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  // Debounced fetch
  const debouncedFetch = useMemo(
    () => debounce(fetchFeatureFlags, 200),
    [fetchFeatureFlags]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const newValue = event.target.value
      setInputValue(newValue)
      setSelectedIndex(-1)
      setSelectedFlag(null)
      debouncedFetch(newValue)
      // Only trigger onChange when input is cleared (empty)
      if (!newValue) {
        onChange(null)
        onFlagSelect?.(null)
      }
    },
    [debouncedFetch, onChange, onFlagSelect]
  )

  const handleSelectFlag = useCallback(
    (flag: FeatureFlag): void => {
      const flagName = flag.name
      setInputValue(flagName)
      setShowDropdown(false)
      setSuggestions([])
      setSelectedFlag(flag)
      onChange(flagName)
      onFlagSelect?.(flag)
    },
    [onChange, onFlagSelect]
  )

  const handleClear = useCallback((): void => {
    setInputValue('')
    setSuggestions([])
    setShowDropdown(false)
    setSelectedFlag(null)
    onChange(null)
    onFlagSelect?.(null)
  }, [onChange, onFlagSelect])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (!showDropdown || suggestions.length === 0) {
        return
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedIndex(prev =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          event.preventDefault()
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          event.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleSelectFlag(suggestions[selectedIndex])
          }
          break
        case 'Escape':
          setShowDropdown(false)
          setSelectedIndex(-1)
          break
        default:
          break
      }
    },
    [showDropdown, suggestions, selectedIndex, handleSelectFlag]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedFlagAdoptionPct = selectedFlag
    ? getAdoptionPercentage(selectedFlag)
    : 0

  return (
    <div className='relative w-full md:flex-1 md:min-w-[280px] md:max-w-[400px]'>
      <div className='relative w-full'>
        <LBInput
          ref={inputRef}
          placeholder='Filter by feature flag'
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowDropdown(true)
            }
          }}
          className='w-full pr-10'
        />
        {inputValue && !isLoading && (
          <LBButton
            onClick={handleClear}
            variant='ghost'
            size='sm'
            className='absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-accent'
          >
            <LBXIcon className='h-4 w-4 text-muted-foreground' />
          </LBButton>
        )}
        {isLoading && (
          <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
            <LBLoader className='h-4 w-4' />
          </div>
        )}
      </div>

      {selectedFlag && !showDropdown && (
        <div className='mt-1 px-1'>
          {/* w-max keeps the row on a single line, overflowing into the empty
              space to the right instead of wrapping inside the 400px input */}
          <div className='flex w-max items-center gap-2'>
            <span className='text-xs text-muted-foreground'>
              ID: {selectedFlag.id}
            </span>
            {selectedFlag.subtext && (
              <InfoTooltip text={selectedFlag.subtext} />
            )}
            <FeatureFlagStatusBadge status={selectedFlag.status} />
            <EnablementBadge percentage={selectedFlagAdoptionPct} />
            {selectedFlag.default_value != null && (
              <span className='text-xs text-muted-foreground'>
                default: {selectedFlag.default_value ? 'True' : 'False'}
              </span>
            )}
            <span className='text-border'>|</span>
            <label
              className='text-xs text-muted-foreground cursor-pointer select-none'
              onClick={() => onInvertedChange?.(!inverted)}
            >
              Inverted
            </label>
            <LBSwitch
              checked={inverted}
              onCheckedChange={checked => onInvertedChange?.(checked)}
            />
            <span className='text-border'>|</span>
            <LBButton
              variant='outline'
              size='sm'
              onClick={() => setJiraSheetOpen(true)}
              className='h-5 px-2 text-[10px]'
            >
              <LBRocketIcon className='h-3 w-3 mr-1' />
              FR Ticket
            </LBButton>
          </div>
        </div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className='absolute z-50 w-full md:w-max md:min-w-full md:max-w-[640px] md:left-auto md:right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto'
        >
          {suggestions.map((flag, index) => (
            <div
              key={flag.id}
              onClick={() => handleSelectFlag(flag)}
              className={`px-3 py-2 cursor-pointer border-b border-border last:border-b-0 ${
                index === selectedIndex ? 'bg-accent' : 'hover:bg-accent'
              }`}
            >
              <div className='flex items-center justify-between gap-2'>
                <span
                  className='text-sm font-medium text-foreground truncate min-w-0'
                  title={flag.name}
                >
                  {flag.name}
                </span>
                <div className='flex items-center gap-2 shrink-0'>
                  <FeatureFlagStatusBadge status={flag.status} />
                  {/* Percentage only — the status badge beside it already says
                      whether the flag is GA */}
                  <EnablementBadge percentage={getAdoptionPercentage(flag)} />
                  <span className='w-8 text-right tabular-nums text-xs text-muted-foreground whitespace-nowrap'>
                    {flag.id}
                  </span>
                </div>
              </div>
              {flag.subtext && (
                <p
                  className='text-xs text-muted-foreground mt-1 truncate max-w-[420px]'
                  title={flag.subtext}
                >
                  {flag.subtext}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <JiraFeatureRolloutSheet
        open={jiraSheetOpen}
        onOpenChange={setJiraSheetOpen}
        flagName={inputValue || ''}
      />
    </div>
  )
}

export default FeatureFlagFilter
